"use client"

import * as React from "react"
import { ChevronDown, FolderOpen } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

interface MemberWorkspaceCellProps {
  member: any
  allWorkspaces: any[]
  userWorkspacesMap: Record<string, string[]>
  fetchUserWorkspaces: (userId: string) => void
  handleToggleUserWorkspace?: (userId: string, workspaceId: string, isAssigned: boolean) => void
}

export function MemberWorkspaceCell({
  member,
  allWorkspaces,
  userWorkspacesMap,
  fetchUserWorkspaces,
}: MemberWorkspaceCellProps) {
  const isOwnerOrAdmin = member.role === "owner" || member.role === "admin"

  React.useEffect(() => {
    if (!isOwnerOrAdmin && member.userId && userWorkspacesMap[member.userId] === undefined) {
      fetchUserWorkspaces(member.userId)
    }
  }, [member.userId, isOwnerOrAdmin, userWorkspacesMap, fetchUserWorkspaces])

  const memberWsIds = userWorkspacesMap[member.userId] || []
  const assignedWorkspaces = React.useMemo(() => {
    if (isOwnerOrAdmin) {
      return allWorkspaces
    }
    return allWorkspaces.filter((w) => memberWsIds.includes(w.id))
  }, [isOwnerOrAdmin, memberWsIds, allWorkspaces])

  if (assignedWorkspaces.length === 0) {
    return null
  }

  return (
    <Popover>
      <PopoverTrigger className="flex items-center gap-1.5 hover:bg-zinc-150 dark:hover:bg-zinc-800 px-2.5 py-1 rounded-lg transition-all text-left max-w-full cursor-pointer border border-transparent hover:border-zinc-200/40 dark:hover:border-zinc-700/40 select-none bg-transparent">
        {isOwnerOrAdmin ? (
          <span className="text-[10px] font-semibold text-[#004EB1] dark:text-blue-400 bg-blue-50 dark:bg-blue-955/30 px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-900/40 flex items-center gap-1">
            <FolderOpen className="size-2.5" />
          </span>
        ) : (
          <div className="flex flex-wrap gap-1 items-center">
            <span
              className="bg-emerald-500/10 text-emerald-600 dark:bg-emerald-955/40 dark:text-emerald-400 font-semibold text-[9px] px-2 py-0.5 rounded-full border border-emerald-500/20 dark:border-emerald-900/30 flex items-center gap-1 max-w-[120px] truncate"
            >
              <FolderOpen className="size-2.5 shrink-0" />
            </span>
          </div>
        )}
        <ChevronDown className="size-3 text-zinc-400 shrink-0" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl font-sans text-xs"
      >
        <div className="flex items-center justify-between border-b border-zinc-100 dark:border-zinc-800/80 pb-2 mb-2">
          <p className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Workspaces Associados
          </p>
          <span className="text-[10px] font-bold text-[#004EB1] dark:text-blue-400 bg-blue-50 dark:bg-blue-955/40 px-1.5 py-0.2 rounded-full">
            {assignedWorkspaces.length}
          </span>
        </div>
        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {assignedWorkspaces.map((ws) => (
            <div
              key={ws.id}
              className="flex items-center gap-2 p-2 rounded-lg bg-zinc-50/70 dark:bg-zinc-800/40 border border-zinc-150 dark:border-zinc-800 text-zinc-800 dark:text-zinc-200"
            >
              <FolderOpen className="size-3.5 text-[#004EB1] dark:text-blue-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-xs truncate">{ws.name}</div>
                {ws.slug && <div className="text-[10px] text-zinc-400 truncate">/{ws.slug}</div>}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
